import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Toolbar,
  IconButton,
  useTheme,
  alpha,
  Tooltip,
  Grid,
  Paper
} from '@mui/material';
import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import RegistrationDetails from '@/components/company/registration-details';
import CompanyInfosForManager from '@/components/company/dashboard/company-infos-for-manager';
import {AccessCode, Company, CompanyStatistics} from '@/types/company';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import BusinessIcon from '@mui/icons-material/Business';
import MenuIcon from '@mui/icons-material/Menu';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import BarChartIcon from '@mui/icons-material/BarChart';
import {useSnackbar} from '@/contexts/snackbar';
import {useMediaQuery} from '@mui/material';
import EmployeesPanel from '@/components/employees';
import {CompanyUser} from '@/types/user';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CompanyStatisticsComponent from '@/components/company/dashboard/company-statistics';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GridViewIcon from '@mui/icons-material/GridView';
import ModuleAdd from '@/components/forms/admin/companies/manage-modules/add-module';
import ExposureIcon from '@mui/icons-material/Exposure';
import ManageModulesForm from '@/components/forms/admin/companies/manage-modules/set-modules';
import TypographyTitle from '@/components/ui/typography-title';
import {useAppSelector} from '@/redux-store/store';

const drawerWidth = 265;
const drawerWidthSmall = 230;
const drawerWidthExtraSmall = 190;
const drawerTop = 80;
const drawerTopSmall = 72;

function useClickOutside(ref: RefObject<HTMLElement>, callback: () => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
}

interface ResponsiveDrawerProps {
  company: Company;
  registerLink: string;
  accessCodes: AccessCode[] | undefined;
  companyEmployees: CompanyUser[] | null;
  companyStatistics: CompanyStatistics | null;
  responsiveAppBarView?: boolean;
}

type ComponentType =
  | 'registration'
  | 'company'
  | 'editForm'
  | 'employees'
  | 'statistics'
  | 'manageModules'
  | 'module';

type DrawerItem = {
  name: string;
  comp: ComponentType;
};

const getIconByComponentType = (comp: ComponentType) => {
  if (comp === 'registration') return <AppRegistrationIcon />;
  if (comp === 'employees') return <SupervisorAccountIcon />;
  if (comp === 'statistics') return <BarChartIcon />;
  if (comp === 'company') return <BusinessIcon />;
  if (comp === 'manageModules') return <ExposureIcon />;
  if (comp === 'module') return <GridViewIcon />;
};

export default function ResponsiveDrawer({
  company,
  registerLink,
  accessCodes,
  companyEmployees,
  companyStatistics,
  responsiveAppBarView
}: ResponsiveDrawerProps) {
  const {t} = useTranslation();

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);

  const isAdmin = userRole && userRole === 'ADMIN';

  const {showSnackbarMessage} = useSnackbar();

  const startView = responsiveAppBarView ? 'company' : 'registration';

  const [drawerOpen, setDrawerOpen] = useState(!isXsScreen);

  const [selectedComponent, setSelectedComponent] =
    useState<ComponentType>(startView);
  const [expanded, setExpanded] = useState(true);

  const handleDrawerToggle = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const drawerRef = useRef(null);
  useClickOutside(drawerRef, () => {
    if (expanded) setExpanded(false);
  });

  const items: DrawerItem[] = [
    {name: t('company_details'), comp: 'company'},
    {name: t('registration_details'), comp: 'registration'},
    {name: t('employee_management'), comp: 'employees'},
    {name: t('company_statistics'), comp: 'statistics'},
    ...(isAdmin
      ? [
          {name: t('manage_modules'), comp: 'manageModules' as ComponentType},
          {name: t('add_module'), comp: 'module' as ComponentType}
        ]
      : [])
  ];

  // const items: DrawerItem[] = [
  //   { name: t('company_details'), comp: 'company' },
  //   { name: t('registration_details'), comp: 'registration' },
  //   { name: t('employee_management'), comp: 'employees' },
  //   { name: t('company_statistics'), comp: 'statistics' },
  //   { name: t('manage_modules'), comp: 'manageModules' },
  //   { name: t('add_module'), comp: 'module' }
  // ];

  const onModulesSet = useCallback(() => {
    showSnackbarMessage(t('modules_set_success'), 'success');
  }, [showSnackbarMessage, t]);

  const onModulesSetError = useCallback(() => {
    showSnackbarMessage(t('modules_set_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const logoSrc = `${company.logo}?timestamp=${Date.now()}`;

  const drawer = (
    <>
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            marginLeft: '-15px',
            marginRight: '12px',
            '&:hover': {
              borderRadius: 0
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      <Toolbar>
        <IconButton
          edge="start"
          onClick={() => setExpanded(!expanded)}
          sx={{
            marginLeft: '-15px',
            marginRight: '12px',
            '&:hover': {
              borderRadius: 0
            }
          }}
        >
          {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {items.map((item) => (
          <ListItem
            key={item.name}
            disablePadding
            sx={{
              marginBottom: '8px',
              backgroundColor:
                selectedComponent === item.comp
                  ? alpha(theme.palette.secondary.light, 0.5)
                  : 'initial'
            }}
          >
            <ListItemButton
              onClick={() => {
                if (selectedComponent === item.comp && !expanded) {
                  setExpanded(true);
                } else {
                  setSelectedComponent(item.comp);
                }
              }}
              selected={selectedComponent === item.comp}
            >
              {expanded ? (
                <ListItemIcon>{getIconByComponentType(item.comp)}</ListItemIcon>
              ) : (
                <Tooltip title={item.name} placement="right">
                  <ListItemIcon>
                    {getIconByComponentType(item.comp)}
                  </ListItemIcon>
                </Tooltip>
              )}
              {expanded && (
                <ResponsiveTypography>{item.name}</ResponsiveTypography>
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
    </>
  );

  return (
    <Box>
      <IconButton
        color="inherit"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          top: 85,
          left: 18,
          zIndex: 1
        }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        ref={drawerRef}
        variant={drawerOpen ? 'permanent' : 'temporary'}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: expanded
              ? {
                  xs: drawerWidthExtraSmall,
                  sm: drawerWidthSmall,
                  md: drawerWidth
                }
              : theme.spacing(8),
            top: {xs: drawerTopSmall, sm: drawerTop}
          }
        }}
      >
        {drawer}
      </Drawer>

      <Box component="main">
        {selectedComponent === 'registration' ? (
          <>
            <TypographyTitle textKey="registration_details" />

            <Paper
              elevation={3}
              sx={{
                marginTop: 4,
                padding: 3
              }}
            >
              <RegistrationDetails
                accessCodes={accessCodes}
                registerLink={registerLink}
              />
            </Paper>
          </>
        ) : selectedComponent === 'company' ? (
          <>
            <Grid item xs={12} marginBottom={5}>
              <Box
                component="img"
                alt={t('company_logo')}
                src={logoSrc}
                sx={{
                  display: 'block',
                  mx: 'auto',
                  maxWidth: {xs: '250px', sm: '300px'},
                  maxHeight: {xs: '100px', sm: '120px'}
                }}
              />
            </Grid>

            <TypographyTitle textKey="company_infos" />
            {/* 
            <Paper
              elevation={3}
              sx={{
                marginTop: 4,
                padding: 3
              }}
            > */}
            <Box marginTop={4}>
              <CompanyInfosForManager
                companyData={company}
                formAdminMode={true}
              />
            </Box>
            {/* </Paper> */}
          </>
        ) : //  : selectedComponent === 'editForm' ? (
        //   <CompanyInfosEditForm
        //     initialData={prepareInitialData(company)}
        //     onSubmit={onCompanyInfosUpdated}
        //     onError={onCompanyInfosUpdateError}
        //     showOnlyTwoButtons={true}
        //     updatingAdminCompany={true}
        //   />
        // )
        selectedComponent === 'employees' && company._id ? (
          <>
            <TypographyTitle textKey="employee_management" />
            <EmployeesPanel
              companyEmployees={companyEmployees}
              companyId={company._id}
              companiesDashboardMode={true}
            />
          </>
        ) : selectedComponent === 'module' && company._id ? (
          <>
            <TypographyTitle textKey="add_module" />
            <Paper
              elevation={3}
              sx={{
                marginTop: 4,
                padding: 3
              }}
            >
              <ModuleAdd showOnlyTwoButtons={true} companyId={company._id} />
            </Paper>
          </>
        ) : selectedComponent === 'manageModules' ? (
          <>
            <TypographyTitle textKey="manage_modules" sx={{marginBottom: 3}} />
            <ManageModulesForm
              onSubmit={onModulesSet}
              onError={onModulesSetError}
              companyData={company}
              showOnlyTwoButtons={true}
            />
          </>
        ) : selectedComponent === 'statistics' ? (
          companyStatistics ? (
            <>
              <TypographyTitle textKey="company_statistics" />

              <Paper
                elevation={3}
                sx={{
                  marginTop: 4,
                  padding: 3
                }}
              >
                <CompanyStatisticsComponent
                  companyStatistics={companyStatistics}
                  sx={{marginTop: 4}}
                />
              </Paper>
            </>
          ) : null
        ) : null}
      </Box>
    </Box>
  );
}
